import {
  Outlet,
  Link,
  createRootRoute,
  HeadContent,
  Scripts,
  useRouterState,
  redirect,
} from "@tanstack/react-router";
import { normalizeSignupPath, rememberFbLandingFromPath } from "@/lib/landing-source";
import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { CartDrawer } from "@/components/CartDrawer";
import { Footer } from "@/components/Footer";
import { SuperSubTelegramWelcomeDialog } from "@/components/vip-chat/SuperSubTelegramWelcomeDialog";
import { Toaster } from "@/components/ui/sonner";
import { queryClient } from "@/lib/query-client";
import { getMetaPixelSnippet, trackPageView } from "@/lib/meta-pixel";

import appCss from "../styles.css?url";

// `__SPA_BUILD__` is injected by `vite.firebase.config.ts` (client-only SPA
// build for Firebase Hosting). The TanStack Start dev server does NOT set it,
// so SSR there keeps its <html>/<head>/<body> shell and linked stylesheet.
declare const __SPA_BUILD__: boolean | undefined;
const IS_SPA_BUILD = typeof __SPA_BUILD__ !== "undefined" && __SPA_BUILD__;

function NotFoundComponent() {
  const handleGoBack = () => {
    if (typeof window !== "undefined") {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.href = "/";
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={handleGoBack}
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go back
          </button>
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  beforeLoad: ({ location }) => {
    if (location.pathname === "/FB4") {
      throw redirect({ to: "/fb4" });
    }
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Lovable App" },
      { name: "description", content: "Lovable Generated Project" },
      { name: "author", content: "Lovable" },
      { property: "og:title", content: "Lovable App" },
      { property: "og:description", content: "Lovable Generated Project" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Lovable" },
    ],
    // In the SPA build, index.html already ships the stylesheet <link>; adding
    // it through HeadContent here would inject a second <link> pointing at the
    // dev path (`/src/styles.css`) and trip React's hydration mismatch check,
    // which in turn freezes Radix dialogs (side menu, notifications, cart).
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      ...(IS_SPA_BUILD ? [] : [{ rel: "stylesheet", href: appCss }]),
    ],
  }),
  // Only the TanStack Start SSR build should render the <html>/<body> shell.
  // In the Firebase SPA build, index.html is the shell, and React must stay
  // below <div id="root">.
  ...(IS_SPA_BUILD ? {} : { shellComponent: RootShell }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        {/* Meta Pixel — SSR shell only; Firebase SPA build uses index.html */}
        <script dangerouslySetInnerHTML={{ __html: getMetaPixelSnippet() }} />
      </head>
      <body>
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src="https://www.facebook.com/tr?id=182393662588232&ev=PageView&noscript=1"
            alt=""
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function MetaPixelRouterTracker() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    rememberFbLandingFromPath(pathname);
    trackPageView();
  }, [pathname]);

  return null;
}

function RootComponent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const hideGlobalFooter = normalizeSignupPath(pathname) === "/fb4";

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CartProvider>
          <MetaPixelRouterTracker />
          <Outlet />
          {!hideGlobalFooter && <Footer />}
          <CartDrawer />
          <SuperSubTelegramWelcomeDialog />
          <Toaster />
        </CartProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
